import { assetUrl } from '~/helpers';
import pluralize from 'pluralize';
import {
  BillingCycle,
  CheckoutPlan,
  Service,
  Subscription,
  SubscriptionStatus,
} from '~/types/models';
import {
  AddOn,
  AddOnCheckoutDetails,
  AddOnItem,
  AddOnType,
} from '~/types/models/addon';
import { StripeCoupon } from '~/types/stripe';
import { capitalize } from 'lodash';

export const billingApi = '/api/v3/billing';

export const DEFAULT_USER_ADD_ON_USD_PRICE = {
  [BillingCycle.Monthly]: 25,
  [BillingCycle.Yearly]: 20,
};

export const AddOnLabels = {
  [AddOnType.TeamMember]: 'Team member users',
  [AddOnType.CustomDomain]: 'Custom domain',
  [AddOnType.LimitGreetRollUp]: 'Growth lite add-on',
};

export const AddOnIntervalLabel = {
  [BillingCycle.Monthly]: 'monthly',
  [BillingCycle.Yearly]: 'annually',
};

export const AddOnIcons = {
  [AddOnType.TeamMember]: {
    [Service.Testimonials]: assetUrl(
      '/assets/images/icons/add-ons/tm-users.svg'
    ),
    [Service.VideoMessaging]: assetUrl(
      '/assets/images/icons/add-ons/team-members.svg'
    ),
  },
  [AddOnType.CustomDomain]: {
    [Service.Testimonials]: assetUrl(
      '/assets/images/icons/add-ons/custom-domain-pink.svg'
    ),
    [Service.VideoMessaging]: assetUrl(
      '/assets/images/icons/add-ons/custom-domain.svg'
    ),
  },
  [AddOnType.LimitGreetRollUp]: {
    [Service.VideoMessaging]: assetUrl(
      '/assets/images/icons/add-ons/group-messaging.svg'
    ),
    [Service.Testimonials]: assetUrl(
      '/assets/images/icons/add-ons/group-messaging.svg'
    ),
  },
};

export const AddOnDescriptions = {
  [AddOnType.TeamMember]: {
    [Service.Testimonials]:
      'Invite colleagues to your team to use Testimonials together.',
    [Service.VideoMessaging]:
      'Invite colleagues to your team to use Bonjoro Video messaging together.',
  },
  [AddOnType.CustomDomain]: {
    [Service.Testimonials]:
      'Change the domain name of your video message landing pages, and testimonial request forms so they are hosted on a URL without reference to Bonjoro.',
    [Service.VideoMessaging]:
      'Change the domain name of your video message landing pages, and testimonial request forms so they are hosted on a URL without reference to Bonjoro.',
  },
  [AddOnType.LimitGreetRollUp]: {
    [Service.VideoMessaging]:
      'Get Roll-ups and remove Bonjoro badge. (For solo users)',
    [Service.Testimonials]:
      'Get Roll-ups and remove Bonjoro badge. (For solo users)',
  },
};

export const AddOnTooltipText = {
  [AddOnType.TeamMember]: 'Added team members',
  [AddOnType.CustomDomain]: 'Custom domain active',
  [AddOnType.LimitGreetRollUp]: 'Roll-ups',
};

export interface GetMonthlyPriceArgs {
  price?: number;
  billingCycle?: BillingCycle;
}

export function getMonthlyPrice({
  price = 0,
  billingCycle,
}: GetMonthlyPriceArgs) {
  const monthlyPrice =
    billingCycle === BillingCycle.Yearly ? price / 12 : price;
  return monthlyPrice % 1 === 0 ? `${monthlyPrice}` : monthlyPrice.toFixed(2);
}

export interface GetDiscountedPriceProps {
  price: number;
  coupon?: StripeCoupon;
}

export function getDiscountedPrice({ price, coupon }: GetDiscountedPriceProps) {
  if (!price) {
    return 0;
  }

  let discountedPrice = price;

  if (coupon) {
    if (coupon?.amountOff) {
      discountedPrice -= (coupon.amountOff as unknown as number) / 100;
    } else if (coupon?.percentOff) {
      discountedPrice -= discountedPrice * (coupon.percentOff / 100);
    }
  }

  return discountedPrice;
}

export function getFormattedPrice(price: number = 0) {
  return price.toFixed(2).replace(/[.,]00$/, '');
}

export interface GetAddOnBillingInfoArgs {
  addOnDetails: AddOnCheckoutDetails;
  subscriptions?: Subscription[];
}

export function getAddOnBillingInfo({
  addOnDetails,
  subscriptions,
}: GetAddOnBillingInfoArgs) {
  let billingInfo = 'You will be billed ';

  const addOnPrice = addOnDetails.addon_price.toFixed(2);
  const totalBill = addOnDetails.total_bill.toFixed(2);
  const isTrialing = subscriptions?.[0]?.status === SubscriptionStatus.Trialing;
  const yearlyBillingCycle = addOnDetails.billing_cycle === BillingCycle.Yearly;

  if (isTrialing) {
    billingInfo += `$${addOnPrice} on ${addOnDetails.billing_date} and $${addOnPrice} `;
  } else if (yearlyBillingCycle) {
    billingInfo += `$${addOnPrice} immediately and $${totalBill} starting on ${addOnDetails.billing_date} and `;
  } else {
    billingInfo += `an additional $${addOnPrice} on your next billing and $${totalBill} starting on ${addOnDetails.billing_date} and `;
  }

  billingInfo += `every ${addOnDetails.billing_cycle}.`;

  return billingInfo;
}

/** https://bonjoro.atlassian.net/browse/PLG-680 */
export function getPlanBillingInfo(plans: CheckoutPlan[]) {
  let billingInfo = 'You will be billed total of ';
  const firstPlan = plans[0];
  const onTrial = firstPlan?.on_trial;
  let totalBill = 0;

  let plansBreakDownInfo = '';
  let projectedAddOnsInfo = '';
  let hasDomainAddOn = true;
  let hasRollUpLimitAddOn = true;
  let domainAddOnPrice = 0;
  let rollupLimitAddOnPrice = 0;

  plans.forEach((plan, planKey) => {
    const addons = plan.addons;
    const service =
      plan.service == Service.Testimonials ? 'Testimonials' : 'Video messaging';

    totalBill += plan.original_price;

    plansBreakDownInfo += `${plan.name} ($${plan.original_price.toFixed(2)})`;

    hasDomainAddOn =
      hasDomainAddOn &&
      typeof addons.find((addOn) => addOn.type === AddOnType.CustomDomain) !==
        'undefined';

    hasRollUpLimitAddOn =
      hasRollUpLimitAddOn &&
      typeof addons.find(
        (addOn) => addOn.type === AddOnType.LimitGreetRollUp
      ) !== 'undefined';

    addons.forEach((addon, addOnKey) => {
      const prefix =
        plans.length - 1 === planKey && addons.length - 1 === addOnKey
          ? ' and'
          : ',';

      totalBill += addon.price;

      if (addon.type === AddOnType.TeamMember) {
        projectedAddOnsInfo += `${prefix} $${addon.price} `;
        projectedAddOnsInfo += `($${plan.additional_seat_price} per user per ${firstPlan.billing_cycle}) `;
        projectedAddOnsInfo += `for additional ${
          addon.quantity
        } ${service} ${pluralize('user', addon.quantity)}`;
      } else if (addon.type === AddOnType.CustomDomain) {
        // show the custom domain on the last plan only.
        domainAddOnPrice = addon.price;
      } else if (addon.type === AddOnType.LimitGreetRollUp) {
        rollupLimitAddOnPrice = addon.price;
      }
    });

    if (plan.service == Service.VideoMessaging && hasRollUpLimitAddOn) {
      projectedAddOnsInfo += ` ${
        hasDomainAddOn ? ',' : 'and'
      } $${rollupLimitAddOnPrice} for Growth lite add-on`;
    }

    const nextPlan = plans[planKey + 1];
    if (nextPlan && nextPlan.total_bill) {
      plansBreakDownInfo += ' and ';
    }
  });

  if (hasDomainAddOn) {
    projectedAddOnsInfo += ` and $${domainAddOnPrice} for Custom domain`;
  }

  const formattedDiscountedTotalBill = applyDiscount(
    totalBill,
    firstPlan.discount?.coupon
  ).toFixed(2);
  billingInfo += `$${formattedDiscountedTotalBill} `;
  billingInfo += 'starting ';
  billingInfo += onTrial ? `on ${firstPlan.trial_ends_at} ` : 'today ';
  billingInfo += `and $${formattedDiscountedTotalBill} every ${firstPlan.billing_cycle} thereafter for `;
  billingInfo += plansBreakDownInfo;

  billingInfo += projectedAddOnsInfo;
  billingInfo += '.';

  if (firstPlan.discount_formatted) {
    billingInfo += ` The total price includes ${firstPlan.discount_formatted} discount.`;
  }

  return billingInfo;
}

function applyDiscount(amount: number, coupon?: StripeCoupon) {
  if (!coupon) {
    return amount;
  }

  const amountOff = (coupon.amountOff ?? 0) / 100;
  const percentOff = coupon.percentOff ?? 0;

  return amountOff ? amount - amountOff : amount - amount * (percentOff / 100);
}

export function mapAddOnToAddOnItem(
  addons: AddOn[],
  service?: Service
): AddOnItem[] {
  return addons.map<AddOnItem>((item) => {
    const uuid = item.uuid;
    const type = item.type;

    let tooltipText = AddOnTooltipText[type];
    let label = AddOnLabels[type];
    if (type === AddOnType.TeamMember) {
      tooltipText = `${item.quantity} ${tooltipText}`;
      if (service) {
        label = `${capitalize(service.replace(/_/g, ' '))} users`;
      }
    }

    return {
      uuid,
      type,
      icon: AddOnIcons[type][service ?? Service.VideoMessaging],
      label: label,
      quantity: item.quantity,
      price: item.price,
      interval: item.interval,
      cycle: item.interval == BillingCycle.Monthly ? '/mo' : '/year',
      billingInfo: `Billed $${item.price} USD ${
        AddOnIntervalLabel[item.interval]
      }`,
      tooltipText,
      description: AddOnDescriptions[type][service ?? Service.VideoMessaging],
      service,
      cancelled: item.cancelled,
      cancelledDaysLeft: item.cancelled_days_left,
      cancelledText: item.cancelled_days_left_text,
    };
  });
}

export function openShopifyCheckoutPage(planSlug: string) {
  window.open(`/billing/shopify/checkout/${planSlug}`, '_blank')?.focus();
}
